import React, { useMemo } from 'react';

const ROOM_TYPE_COLORS = {
  living_room: 'bg-green-500',
  bedroom: 'bg-blue-500',
  kitchen: 'bg-orange-500',
  bathroom: 'bg-cyan-500',
  dining_room: 'bg-yellow-500',
  office: 'bg-purple-500',
  hallway: 'bg-gray-500',
  garage: 'bg-gray-600',
  laundry: 'bg-blue-400',
  balcony: 'bg-lime-500',
};

const ROOM_TYPE_LABELS = {
  living_room: 'Sala de Estar',
  bedroom: 'Quarto',
  kitchen: 'Cozinha',
  bathroom: 'Banheiro',
  dining_room: 'Sala de Jantar',
  office: 'Escritório',
  hallway: 'Corredor',
  garage: 'Garagem',
  laundry: 'Lavanderia',
  balcony: 'Varanda',
};

export const AdminDashboardAnalyticsTab = ({ allProjects }) => {
  const userProjects = useMemo(() => {
    const groupedByUser = allProjects.reduce((acc, project) => {
      const key = project.user_email || 'sem-email';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(groupedByUser).sort((a, b) => b[1] - a[1]);
  }, [allProjects]);

  const roomTypeDistribution = useMemo(() => {
    const groupedByRoomType = {};

    allProjects.forEach((project) => {
      project.floor_plan?.rooms?.forEach((room) => {
        groupedByRoomType[room.room_type] = (groupedByRoomType[room.room_type] || 0) + 1;
      });
    });

    return Object.entries(groupedByRoomType).sort((a, b) => b[1] - a[1]);
  }, [allProjects]);

  const maxProjectCount = userProjects.length ? Math.max(...userProjects.map(([, count]) => count)) : 1;

  return (
    <div className="space-y-6" data-testid="admin-dashboard-analytics-tab">
      <div className="bg-white rounded-xl shadow-sm" data-testid="admin-dashboard-analytics-users-card">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900" data-testid="admin-dashboard-analytics-users-title">👥 Projetos por Usuário</h2>
        </div>
        <div className="p-6">
          <div className="space-y-3" data-testid="admin-dashboard-analytics-users-list">
            {userProjects.map(([email, count]) => (
              <div key={email} className="flex items-center gap-4" data-testid={`admin-dashboard-user-project-row-${email.replace(/[^a-zA-Z0-9]/g, '-')}`}>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-700" data-testid={`admin-dashboard-user-email-${email.replace(/[^a-zA-Z0-9]/g, '-')}`}>{email}</span>
                    <span className="text-sm font-medium text-gray-900" data-testid={`admin-dashboard-user-count-${email.replace(/[^a-zA-Z0-9]/g, '-')}`}>{count} projetos</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full"
                      style={{ width: `${(count / maxProjectCount) * 100}%` }}
                      data-testid={`admin-dashboard-user-progress-${email.replace(/[^a-zA-Z0-9]/g, '-')}`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm" data-testid="admin-dashboard-analytics-room-types-card">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900" data-testid="admin-dashboard-analytics-room-types-title">🏠 Distribuição de Tipos de Cômodos</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="admin-dashboard-analytics-room-types-grid">
            {roomTypeDistribution.map(([roomType, count]) => (
              <div key={roomType} className="text-center p-4 bg-gray-50 rounded-lg" data-testid={`admin-dashboard-room-type-card-${roomType}`}>
                <div className={`w-12 h-12 ${ROOM_TYPE_COLORS[roomType] || 'bg-gray-400'} rounded-full mx-auto mb-2 flex items-center justify-center text-white font-bold`} data-testid={`admin-dashboard-room-type-count-${roomType}`}>
                  {count}
                </div>
                <p className="text-sm text-gray-700" data-testid={`admin-dashboard-room-type-label-${roomType}`}>{ROOM_TYPE_LABELS[roomType] || roomType}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
